import { safeExternalUrl } from './format.js';

/**
 * 폼 검증 공용 헬퍼 (경품·파친코·랜덤박스 경품/상품 화면이 같이 쓴다).
 *
 * 서버는 `createPrizeCrud` / `createProductCrud` 가 필드 정의로 zod 스키마를
 * 만들어 검사한다 (server/src/modules/shared/{prize,product}-crud.ts).
 * 여기 있는 건 그 규칙을 눌러보기 전에 알려주는 것뿐이고, **서버가 여전히 권위다.**
 *
 * 이미지 주소를 http(s) 로 못박는 이유가 하나 더 있다: 목록 화면이 이 값을
 * `<a href>` 로 내보내는데, SafeLink 가 http(s) 가 아니면 링크를 만들지 않는다.
 * 여기서 막지 않으면 관리자는 저장에 성공해 놓고 나중에 "링크가 안 걸린다"를
 * 겪는다 - 저장할 때 알려주는 편이 낫다.
 */

export type FieldErrors = Record<string, string | undefined>;

/** 오류가 하나라도 있으면 true. */
export const hasFieldErrors = (errors: FieldErrors): boolean =>
  Object.values(errors).some((v) => v !== undefined);

/** 오류가 난 입력에 붙일 class. */
export const inputCls = (error?: string): string => (error ? 'input input--error' : 'input');

/** 이미지·동영상 주소 칸. 비워 두는 건 괜찮다 - 서버도 빈 문자열을 받는다. */
export function checkImageUrl(value: string, maxLength = 255): string | undefined {
  const url = value.trim();
  if (url === '') return undefined;
  if (url.length > maxLength) return `주소는 ${maxLength}자까지 쓸 수 있습니다.`;
  if (!safeExternalUrl(url)) return 'http:// 또는 https:// 로 시작하는 주소를 넣어주세요.';
  return undefined;
}

/** 글자 수만 보는 칸. 서버 필드 정의의 maxLength 와 같은 값을 넘긴다. */
export function checkLen(value: string, maxLength: number, label: string): string | undefined {
  if (value.trim().length > maxLength) return `${label}은(는) ${maxLength}자까지 쓸 수 있습니다.`;
  return undefined;
}

/**
 * 확률(%). 서버 필드 정의는 그냥 int 라 음수도 500 도 통과한다.
 * 확률이라는 뜻에 맞게 0~100 으로 제한한다.
 */
export function checkPercent(value: string): string | undefined {
  const text = value.trim();
  if (text === '') return undefined;
  if (!/^\d+$/.test(text)) return '확률은 0 이상의 정수로 입력해주세요.';
  if (Number(text) > 100) return '확률은 100 이하로 입력해주세요.';
  return undefined;
}

/**
 * DECIMAL(5,2) 확률 칸 (lottery_prizes.drawProbability). 8.20·0.10 같은 값이 흔해
 * 정수만 받는 checkPercent 를 쓰면 그런 경품은 저장할 수 없다.
 */
export function checkProbability(value: string): string | undefined {
  const text = value.trim();
  if (text === '') return undefined;
  if (!/^\d+(\.\d{1,2})?$/.test(text)) return '확률은 0 이상의 숫자(소수점 둘째 자리까지)로 입력해주세요.';
  if (Number(text) > 100) return '확률은 100 이하로 입력해주세요.';
  return undefined;
}

/** 반드시 채워야 하는 칸. 서버 필드 정의의 `required: true` 와 짝이다. */
export function checkRequired(value: string, label: string, maxLength?: number): string | undefined {
  if (value.trim() === '') return `${label}을(를) 입력해주세요.`;
  if (maxLength !== undefined) return checkLen(value, maxLength, label);
  return undefined;
}

/**
 * 기간 두 칸. 시작이 종료보다 뒤면 막는다.
 *
 * 서버는 두 날짜를 각각만 보고 순서는 안 본다. 뒤집힌 채로 저장되면 판매 기간이
 * 통째로 비는데, 화면 어디에도 그 사실이 안 드러나 찾기 어렵다.
 */
export function checkDateRange(from: string, to: string): string | undefined {
  if (from.trim() === '' || to.trim() === '') return undefined;
  return from > to ? '종료일이 시작일보다 앞설 수 없습니다.' : undefined;
}
